import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { EditableComponent } from '@adobe/aem-react-editable-components'
import {
  AddToCart,
  MixAndMatchHeader as UIMixAndMatchHeader,
  MixAndMatch as UIMixAndMatch,
  MiniCart,
  StockStatuses,
  ColorSelectorTypes,
  useIsMobile
} from '@dcxwam/dcx-wam-ui-foundations'
import { AttributeSelectorItemProps } from '@dcxwam/dcx-wam-ui-foundations/build/types/components/organisms/product/common/attribute-selector/types'
import type { ColorProps } from '@dcxwam/dcx-wam-ui-foundations/build/types/components/organisms/product/common/color-selector/types'
import type { ChangeEvent, MouseEvent } from 'react'
import { useCookies } from 'react-cookie'
import type {
  AppConfig,
  HybrisConfig,
  MuleSoftConfigType
} from '../../../utils/page-config'
import type { ShopConfig } from '../common/config'
import withComponentRefresh from '../../../utils/aem/editor/withComponentRefresh'
import { useExtractEditableProps } from '../../../utils/aem/editor/extractEditableProps'
import {
  withErrorBoundary,
  ErrorFallback
} from '../../functional/ErrorBoundary'
import MixAndMatchEditConfig from './MixAndMatchEditConfig'
import {
  EditableComponentPropsType,
  GetCAndBHeadDataReturnType
} from '../../../utils/prop-types'
import {
  ErrorHandlingState,
  ExpandedStepDescription,
  MixAndMatchModelProps,
  ProductDataState,
  ProductInfoProps,
  ProductSelectionState,
  StepCurrentVariantMap,
  StepDescriptionModelProps,
  StepModelProps,
  WizardStepConfigProps,
  WizardUIState
} from './types'
import {
  COLOR_SELECTOR_ID_SUFIX,
  GIGYA_COOKIE_JWT,
  GIGYA_COOKIE_UID,
  HYBRIS_ACCESS_TOKEN,
  REPLACE_AMOUNT,
  REPLACE_INDEX
} from '../../../utils/constants'
import {
  fetchStockData,
  fetchProductData
} from '../../../services/product/productService'
import ExperienceFragmentContent from '../../templates/ExperienceFragmentContent/ExperienceFragmentContent'
import { useIsSsr } from '../../../utils/isSsr'
import { PageConfig } from '../../../utils/page-config'
import {
  getPreviewHeaders,
  getServerSideHeaders
} from '../../../utils/api-headers'
import { setBreadcrumbTitle } from '../../../utils/breadcrumb-context'
import {
  ProductInfo,
  ProductDataVariant,
  ProductSetReference,
  VariantWithDynamicAttributes
} from '../../../services/product/product-data'
import { getProductImageGalleryProps } from '../common/product-image-gallery'
import { getAssetImageUrl } from '../../../utils/asset-url'
import { getProductDescription } from '../common/product-description'
import { getStockInfoLabel } from '../common/stock-info'
import {
  getFlavourProfiles,
  getPriceInfo,
  stockStatusMap
} from '../ProductList/utils'
import {
  getAttributesSelectorProps,
  getColorSelectorProperties,
  handleAttributeSelectorChange,
  splitProductCode
} from '../common/variant-selector'
import { AttributeSelectorItem } from '../common/types'
import { ProductStockData, StockLevelStatus } from '../../../services/stock'
import { setMarkerProductData } from '../../../utils/marker-product-data'
import {
  createAddToCartMethodConfig,
  getChildProductEntries,
  getProductSetEntry,
  useMiniCartConfig,
  CartProductEntry,
  SetCookieType,
  SetErrorMessageContentType
} from '../common/cart-management/cartConfig'
import { useLocalStorageCart } from '../../../services/product/use-cart-local-storage'
import CartErrorMessageDialog from '../common/cart-management/CartErrorMessageDialog'
import { handleAddToCart } from '../common/cart-management/handleAddToCart'
import { getDeliveryMessage } from '../common/delivery-message'
import {
  createDataLayerEcommerceProduct,
  pushDataLayerProductDetailsView
} from '../common/datalayer/product'
import { generateProductJsonLD } from '../common/product-jsonld'
import { UserDataContext } from '../../../services/user/user-data'

const updateCompletionMap = (completionMap: boolean[], index: number) =>
  completionMap?.map((isCompleted, completionIndex) =>
    index === completionIndex ? true : isCompleted
  )

const isAttributeMatching = (
  variant: ProductDataVariant,
  attrName: string,
  expectedValue: string
): boolean => {
  const variantValue = (variant as VariantWithDynamicAttributes)?.[attrName]
  return variantValue === expectedValue
}

const hasAllMatchingAttributes = (
  variant: ProductDataVariant,
  attributeValues: Record<string, string>
): boolean => {
  const attributeKeys = Object.keys(attributeValues)
  return attributeKeys.every((attr) =>
    isAttributeMatching(variant, attr, attributeValues[attr])
  )
}
const getAugmentedStepWithVariant = (
  step: StepCurrentVariantMap,
  stepId: string,
  variantToSelect: ProductDataVariant
) =>
  step.stepId === stepId ? { ...step, currentVariant: variantToSelect } : step

const extractAttributeValues = (
  variant: ProductDataVariant,
  attributes: AttributeSelectorItem[]
): Record<string, string> =>
  attributes.reduce(
    (acc, attr) => {
      const value = (variant as VariantWithDynamicAttributes)[attr.name]
      return {
        ...acc,
        [attr.name]: typeof value === 'string' ? value : ''
      }
    },
    {} as Record<string, string>
  )

const findVariantByColorAndAttributes = ({
  stepVariants,
  newVariant,
  attributeValues,
  groupByColorOrFlavour
}: {
  stepVariants: ProductDataVariant[] | undefined
  newVariant: ProductDataVariant
  attributeValues: Record<string, string>
  groupByColorOrFlavour?: boolean
}): ProductDataVariant | undefined =>
  stepVariants?.find((variant) => {
    const isSameColor = groupByColorOrFlavour
      ? variant.nuance?.code === newVariant.nuance?.code
      : splitProductCode(variant.pmiInternalProductCode ?? '') ===
        splitProductCode(newVariant.pmiInternalProductCode ?? '')

    return isSameColor && hasAllMatchingAttributes(variant, attributeValues)
  })

const expandStepsDescription = ({
  stepsDescription,
  productSetReferences
}: {
  stepsDescription: StepDescriptionModelProps[]
  productSetReferences?: ProductSetReference[]
}): ExpandedStepDescription[] =>
  stepsDescription.flatMap((stepDescription) => {
    const { groupId } = stepDescription
    const productSetReference = productSetReferences?.find(
      (psr) => psr?.group?.toString() === groupId
    )

    // When product data is available, skip steps without a matching reference
    if (productSetReferences?.length && !productSetReference) {
      return []
    }

    const quantity = productSetReference?.quantity ?? 1

    return Array.from({ length: quantity }, (_, quantityIndex) => ({
      ...stepDescription,
      expandedStepId: `${groupId}_${quantityIndex}`,
      quantityIndex
    }))
  })

const getMappedModelStepsToWizardSteps = ({
  stepsModel,
  expandedSteps,
  productInfoList,
  productStockMap,
  onSelect,
  onChange,
  shopConfig,
  showFlavourProfiles,
  backupGalleryUrl,
  enableGalleryLazyLoad,
  stepCurrentVariantMap,
  onColorChange,
  attributeSelector,
  onAttributeChange,
  pageLocale,
  groupByFlavour,
  isSsr,
  id,
  productData
}: {
  stepsModel: StepModelProps
  expandedSteps: ExpandedStepDescription[]
  productInfoList: ProductInfoProps[]
  productStockMap: Map<string, ProductStockData>
  onSelect: (index: number) => void
  onChange: (index: number) => void
  shopConfig: ShopConfig
  showFlavourProfiles: boolean
  backupGalleryUrl?: string
  enableGalleryLazyLoad: boolean
  stepCurrentVariantMap: StepCurrentVariantMap[]
  onColorChange: (stepId: string, newVariant: ProductDataVariant) => void
  attributeSelector?: AttributeSelectorItem[]
  onAttributeChange: (
    newProductAttributeSelectedValues: Record<string, string>,
    currentProductVariant: ProductDataVariant,
    expandedStepId: string
  ) => void
  pageLocale?: string
  groupByFlavour?: boolean
  isSsr: boolean
  id: string
  productData?: ProductInfo
}): WizardStepConfigProps[] =>
  expandedSteps?.map(
    (stepDescription: ExpandedStepDescription, index: number) => {
      const { groupId, expandedStepId } = stepDescription

      const productSetReference = productData?.productSetReferences?.find(
        (psr) => psr?.group?.toString() === groupId
      )
      const productVariantIds = productSetReference?.globalArticleCode ?? []

      const productVariants = productVariantIds
        .map((variantId) =>
          productInfoList?.[index]?.variants?.find(
            (v) => v.globalArticleCode === variantId
          )
        )
        .filter((v): v is ProductDataVariant => Boolean(v))

      const xfContent =
        stepDescription?.productUSPs || stepDescription?.productUSPsXFContent
          ? {
              model: {
                xfPath: `${stepDescription?.productUSPs}`,
                xfContent: stepDescription?.productUSPsXFContent
                  ? { ...stepDescription?.productUSPsXFContent }
                  : undefined
              }
            }
          : undefined

      const currentStepVariant = stepCurrentVariantMap?.find(
        (step) => step.stepId === expandedStepId
      )
      const currentProductVariant =
        currentStepVariant?.currentVariant || productVariants?.[0]

      const productImageGalleryProps = getProductImageGalleryProps({
        productVariant: currentProductVariant,
        backupImageUrl: backupGalleryUrl,
        enableLazyLoad: enableGalleryLazyLoad
      })
      const currentColorName = currentProductVariant?.nuance?.description
      const currentProductVariantStockLevelStatus = productStockMap.get(
        currentProductVariant?.globalArticleCode ?? ''
      )?.stock?.stockLevelStatus
      const stockInfoLabel = getStockInfoLabel({
        isSsr,
        stockLevelStatus: currentProductVariantStockLevelStatus ?? '',
        shopConfig
      })
      const showStockInfo = Boolean(currentProductVariantStockLevelStatus)
      const stockInfo = showStockInfo
        ? {
            stockLabel: stockInfoLabel,
            stockStatus: currentProductVariantStockLevelStatus
              ? stockStatusMap[currentProductVariantStockLevelStatus]
              : undefined,
            showStockMessage: currentProductVariantStockLevelStatus
              ? stockStatusMap[currentProductVariantStockLevelStatus] !==
                StockStatuses.InStock
              : shopConfig?.showInStockInfoEnable
          }
        : undefined

      const onColorVariantChange = (
        e: MouseEvent,
        clickedColor: ColorProps
      ) => {
        e.preventDefault()
        const currentElement = productVariants?.find((variant) => {
          if (groupByFlavour) {
            return variant.nuance?.code === clickedColor.value
          }

          return (
            splitProductCode(variant.pmiInternalProductCode ?? '') ===
            clickedColor.value
          )
        })

        if (currentElement && expandedStepId) {
          onColorChange(expandedStepId, currentElement)
        }
      }

      const attributeSelectorMap =
        attributeSelector?.reduce(
          (acc, attr: AttributeSelectorItem) => ({
            ...acc,
            [attr.name]: attr.label
          }),
          {}
        ) ?? {}

      const attributeNames = Object.keys(attributeSelectorMap)
      const attributeNameOptions =
        productVariants?.reduce(
          (acc, variant) => {
            const newAttributesAcc = { ...acc }
            attributeNames.forEach((attributeName) => {
              const options = acc[attributeName] ?? []
              const variantValue = (variant as VariantWithDynamicAttributes)[
                attributeName
              ]
              if (
                typeof variantValue === 'string' &&
                !options.includes(variantValue)
              ) {
                options.push(variantValue)
              }
              newAttributesAcc[attributeName] = options
            })
            return newAttributesAcc
          },
          {} as Record<string, string[]>
        ) ?? {}

      // Extract current attribute values from the current variant
      const currentAttributeSelectedValues = currentProductVariant
        ? attributeNames.reduce(
            (acc, attrName) => {
              const value = (
                currentProductVariant as VariantWithDynamicAttributes
              )[attrName]
              return {
                ...acc,
                [attrName]: typeof value === 'string' ? value : ''
              }
            },
            {} as Record<string, string>
          )
        : {}

      const colorSelectorProps = getColorSelectorProperties({
        variants: productVariants,
        currentProductStockMap: productStockMap,
        productRootPath: '',
        generalConfig: { groupByFlavour },
        currentAttributeSelectedValues: {},
        outOfStockValue: 'Out of Stock',
        attributeNames,
        currentProductVariant,
        colorName: currentColorName,
        type: ColorSelectorTypes.Hyperlink,
        colorGroupAriaLabel: shopConfig?.colorGroupAriaLabel,
        onChange: onColorVariantChange as (
          e: ChangeEvent<HTMLInputElement> | React.MouseEvent,
          clickedColor: ColorProps
        ) => void,
        id: id
          ? `${id}-${COLOR_SELECTOR_ID_SUFIX}`
          : `mam-${COLOR_SELECTOR_ID_SUFIX}`
      })

      const onAttributeSelectorChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        attributeOption: AttributeSelectorItemProps
      ) => {
        e.preventDefault()
        attributeNames.forEach((attributeName) => {
          const { clickedVariant, newProductAttributeSelectedValues } =
            handleAttributeSelectorChange({
              attributeOption,
              config: { groupByFlavour },
              productVariants: productVariants ?? [],
              attributeNames,
              currentAttributeSelectedValues,
              attributeName,
              currentProductVariant:
                currentProductVariant &&
                Object.keys(currentProductVariant).length > 0
                  ? currentProductVariant
                  : ({} as ProductDataVariant)
            })

          if (clickedVariant && currentProductVariant) {
            onAttributeChange?.(
              newProductAttributeSelectedValues,
              currentProductVariant,
              expandedStepId
            )
          }

          return null
        })
      }

      const attributeSelectorProps = getAttributesSelectorProps({
        attributeNames,
        attributeSelectorMap,
        currentAttributeSelectedValues,
        attributeNameOptions,
        markers: shopConfig?.attributeSelectorMarkers,
        generalConfig: { groupByFlavour },
        currentProductVariant: currentProductVariant ?? undefined,
        variants: productVariants,
        attributeSelector,
        onChange: onAttributeSelectorChange,
        pageLocale,
        currentProductStockMap: productStockMap
      })

      const hasVariationChoice = !!(
        productVariants &&
        productVariants?.length > 0 &&
        (attributeSelectorProps?.some(
          (asp) => asp?.attributeVariants && asp?.attributeVariants?.length > 1
        ) ||
          (colorSelectorProps?.colorVariants &&
            colorSelectorProps?.colorVariants?.length > 1))
      )
      const flavourProfiles = getFlavourProfiles(
        currentProductVariant ?? ({} as ProductDataVariant),
        shopConfig,
        true
      )

      const productDescription = getProductDescription(currentProductVariant)
      const isCurrentSelectionOutOfStock =
        stockInfo?.stockStatus === stockStatusMap[StockLevelStatus.outOfStock]
      const possibleStepTitle =
        stepDescription?.stepTitle || stepsModel?.stepDefaultTitle

      return {
        globalArticleCode: currentProductVariant?.globalArticleCode,
        getTitle: (stepIndex: number, stepAmount: number) =>
          `<b>${
            stepsModel?.stepCounterLabel
              ?.replace(REPLACE_INDEX, String(stepIndex + 1))
              ?.replace(REPLACE_AMOUNT, String(stepAmount)) ?? ''
          }</b> ${possibleStepTitle ?? ''}`,
        selectionButtonProps: {
          label: isCurrentSelectionOutOfStock
            ? shopConfig?.outOfStockLabel
            : stepDescription?.ctaLabel || stepsModel?.ctaDefaultLabel,
          onClick: () => onSelect(index),
          ...(isCurrentSelectionOutOfStock ? { disable: true } : {})
        },
        description: productDescription,
        selectionTitle: productInfoList?.[index]?.productTitle,
        selectionSubtitle: productInfoList?.[index]?.productSubtitle,
        selectionImage: productInfoList?.[index]?.productImage,
        changeLinkProps: {
          label: hasVariationChoice
            ? stepsModel?.changeActionLabel
            : stepsModel?.detailsActionLabel,
          onClick: () => onChange(index)
        },
        productTitle: currentProductVariant?.globalArticleName,
        productSubtitle: currentProductVariant?.nuance?.name,
        colorSelectorNote: stepDescription?.colorSelectorNote,
        productImageGalleryProps,
        productColorSelectorProps: hasVariationChoice
          ? { ...colorSelectorProps }
          : undefined,
        productStockInfoProps: stockInfo,
        productAttributeSelectorPropsList: hasVariationChoice
          ? attributeSelectorProps
          : undefined,
        ...(showFlavourProfiles
          ? {
              productFlavourProfilesProps: flavourProfiles
            }
          : {}),
        isCompleted:
          !hasVariationChoice &&
          stockInfo !== undefined &&
          !isCurrentSelectionOutOfStock,
        additionalContent: xfContent ? (
          <ExperienceFragmentContent {...xfContent} />
        ) : null
      }
    }
  )

const MixAndMatch: React.FC<EditableComponentPropsType<MixAndMatchModelProps>> =
  withComponentRefresh((props) => {
    const {
      id,
      key,
      wizardSteps,
      ratesAndReviews,
      general,
      initialProductData,
      highlightComponent,
      pageConfig,
      attributeSelector = [],
      deliveryMessage,
      ...rest
    } = props.model
    const editableProps = useExtractEditableProps(props, rest)
    const pageLocale = pageConfig?.locale
    const caConfig = PageConfig?.getConfig(
      PageConfig?.ConfigObjects as unknown as Parameters<
        typeof PageConfig.getConfig
      >[0]
    ) as AppConfig | null
    const isSsr = useIsSsr()
    const isMobile = useIsMobile()
    const [cartLocalStorageJSON, setCartLocalStorage] = useLocalStorageCart()
    const userData = useContext(UserDataContext)
    const [cookies, setCookie] = useCookies([
      GIGYA_COOKIE_UID,
      HYBRIS_ACCESS_TOKEN,
      GIGYA_COOKIE_JWT
    ])

    const stepRefs = useRef<(HTMLDivElement | null)[]>([])
    const headerRef = useRef<HTMLDivElement>(null)
    const [isWizardHeaderMounted, setIsWizardHeaderMounted] =
      useState(!!initialProductData)

    useEffect(() => {
      if (!initialProductData && headerRef.current && !isWizardHeaderMounted) {
        setIsWizardHeaderMounted(true)
      }
    })

    const scrollToElement = (element: HTMLElement | null, offset = 0) => {
      if (element) {
        window.dispatchEvent(new CustomEvent('programmaticScrollStart'))

        const boundingRect = element.getBoundingClientRect()
        if (boundingRect) {
          const elementPosition = boundingRect.top + window.pageYOffset
          const offsetPosition = elementPosition + offset

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          })

          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('programmaticScrollEnd'))
          }, 1000)
        }
      }
    }

    const expandedSteps = useMemo(
      () =>
        expandStepsDescription({
          stepsDescription: wizardSteps?.stepsDescription ?? [],
          productSetReferences: initialProductData?.productSetReferences
        }),
      [wizardSteps?.stepsDescription, initialProductData?.productSetReferences]
    )

    const initialVariantIndexes =
      expandedSteps?.map((step) => ({
        stepId: step.expandedStepId,
        currentVariant: null
      })) ?? []

    const [selectionState, setSelectionState] = useState<ProductSelectionState>(
      {
        stepCurrentVariantMap: initialVariantIndexes,
        // Initialize temp state with same values as "committed" state
        tempStepCurrentVariantMap: initialVariantIndexes
      }
    )

    const [productState, setProductState] = useState<ProductDataState>({
      productData: initialProductData,
      productStockMap: new Map(
        initialProductData?.variants
          ?.filter((v) => v?.globalArticleCode)
          ?.map((productVariant) => [
            productVariant.globalArticleCode!,
            {
              code: productVariant.globalArticleCode!,
              stock: productVariant?.stock
            } as ProductStockData
          ]) ?? []
      ),
      isStockUpdated: undefined
    })

    const [wizardUIState, setWizardUIState] = useState<WizardUIState>({
      steps: [],
      completionMap: [],
      activeStepIndex: 0,
      activeMiniCart: false,
      isButtonLoading: false,
      isButtonDisabled: isSsr
    })

    useEffect(() => {
      setWizardUIState((prev) => ({
        ...prev,
        isButtonDisabled: isSsr
      }))
    }, [isSsr])

    const [errorHandlingState, setErrorHandlingState] =
      useState<ErrorHandlingState>({
        dialogErrorMessageIsOpen: false,
        errorMessageContent: {
          message: null,
          title: caConfig?.shopConfig?.addToCartErrorMessage ?? null,
          actions: null
        }
      })

    const alignSelectionOnStock = (stockMap: Map<string, ProductStockData>) => {
      const currentExpandedSteps = expandStepsDescription({
        stepsDescription: wizardSteps?.stepsDescription ?? [],
        productSetReferences: productState.productData?.productSetReferences
      })
      const newStepCurrentVariantMap =
        currentExpandedSteps?.map((step) => {
          const { groupId, expandedStepId } = step
          const productSetReference =
            productState.productData?.productSetReferences?.find(
              (psr) => psr?.group?.toString() === groupId
            )
          const productVariantIds = productSetReference?.globalArticleCode ?? []

          // Get all variants for this step
          const variants = productState.productData?.variants?.filter((v) =>
            productVariantIds.includes(v?.globalArticleCode ?? '')
          )

          // Check if there's a variant matching initial productData that is in stock
          const initialVariantInStock = variants?.find(
            (variant) =>
              variant?.globalArticleCode ===
                initialProductData?.globalArticleCode &&
              (stockMap?.get(variant?.globalArticleCode ?? '')?.stock
                ?.stockLevel ?? 0) > 0
          )

          // If not, find first in-stock variant
          const firstInStockVariant =
            initialVariantInStock ||
            variants?.find(
              (variant) =>
                (stockMap?.get(variant?.globalArticleCode ?? '')?.stock
                  ?.stockLevel ?? 0) > 0
            ) ||
            variants?.[0]

          return {
            stepId: expandedStepId,
            currentVariant: firstInStockVariant || null
          }
        }) ?? []

      setSelectionState((prev) => ({
        ...prev,
        stepCurrentVariantMap: newStepCurrentVariantMap,
        // Also update temp state to match
        tempStepCurrentVariantMap: newStepCurrentVariantMap
      }))

      setProductState((prev) => ({
        ...prev,
        productStockMap: stockMap
      }))
    }

    const fetchSData = async (productGcodes: string[]) => {
      if (!caConfig?.cbxConfig) {
        return
      }
      const customHeaders = getPreviewHeaders()

      const stockData = await fetchStockData({
        config: caConfig.cbxConfig,
        articleList: Array.from(productGcodes),
        customHeaders
      })
      let productStockMap
      if (stockData?.length) {
        productStockMap = new Map(
          stockData?.map((productStock) => [productStock.code, productStock])
        )
      } else {
        productStockMap = productState.productStockMap
      }
      alignSelectionOnStock(productStockMap)
      setProductState((prev) => ({
        ...prev,
        isStockUpdated: !!stockData?.length
      }))
    }

    let configMiniCart = null
    if (
      !(caConfig?.shopConfig as ShopConfig & { redirectToCart?: boolean })
        ?.redirectToCart
    ) {
      const productEntry = getProductSetEntry(
        cartLocalStorageJSON?.cartModifications,
        cartLocalStorageJSON?.cart
      )
      const childProductEntries = getChildProductEntries(
        cartLocalStorageJSON?.cartModifications,
        cartLocalStorageJSON?.cart
      )
      configMiniCart = useMiniCartConfig({
        productEntry,
        ...((
          caConfig?.shopConfig as ShopConfig & {
            showOnlyProductSetInMiniCart?: boolean
          }
        )?.showOnlyProductSetInMiniCart
          ? {}
          : {
              childProductEntries: childProductEntries?.filter(
                (e): e is CartProductEntry => Boolean(e)
              )
            }),
        config: {
          successMessage: caConfig?.shopConfig?.successMessage ?? '',
          checkoutCTALabel: caConfig?.shopConfig?.checkoutCTALabel ?? '',
          checkoutCTAUrl: caConfig?.shopConfig?.checkoutCTAUrl ?? '',
          cartCTALabel: caConfig?.shopConfig?.cartCTALabel ?? '',
          cartCTAUrl: caConfig?.shopConfig?.cartCTAUrl ?? '',
          closeButtonAriaLabel:
            caConfig?.shopConfig?.closeButtonAriaLabel ?? '',
          showOldPrices: general?.showOldPrices ?? false
        },
        variants: (
          productState.productData?.variants?.concat?.(
            productState.productData as ProductDataVariant
          ) ?? []
        )
          ?.filter(
            (v): v is ProductDataVariant & { globalArticleCode: string } =>
              Boolean(v?.globalArticleCode)
          )
          ?.map((v) => ({
            globalArticleCode: v.globalArticleCode!,
            assets: v.assets?.map((asset) => ({
              assetFullUrl: asset.assetFullUrl ?? '',
              assetRelativePath: asset.assetRelativePath ?? '',
              assetType: asset.assetType ?? ''
            }))
          })), // Add Product set info to the variant list
        cartLocalStorageJSON,
        activeMiniCart: wizardUIState.activeMiniCart,
        setActiveMiniCart: (active: boolean) =>
          setWizardUIState((prev) => ({ ...prev, activeMiniCart: active })),
        productQuantityLabel: (caConfig?.shopConfig?.quantityLabel ??
          '') as string
      })
    }

    const deliveryInfoMessage = getDeliveryMessage(deliveryMessage || {}, isSsr)

    const isCompletionFull =
      wizardUIState.completionMap?.length > 0 &&
      wizardUIState.completionMap?.filter((completion) => !completion)
        ?.length === 0

    useEffect(() => {
      // Fallback product call on CSR when no server data is given
      if (!initialProductData) {
        const fetchPData = async () => {
          if (!caConfig?.cbxConfig) {
            return
          }
          const customHeaders = getPreviewHeaders()

          const productData = await fetchProductData<ProductInfo>({
            config: caConfig.cbxConfig,
            key: key ?? '',
            includeVariants: 'true',
            includeRelationships: 'true',
            includeOffline: 'false',
            includeCrosslinked: 'false',
            customHeaders
          })
          setProductState((prev) => ({
            ...prev,
            productData
          }))
        }
        fetchPData()
      }
    }, [])

    useEffect(() => {
      if (productState.productData) {
        fetchSData(
          productState.productData?.variants?.map(
            (variant) => variant?.globalArticleCode ?? ''
          ) ?? []
        )
        setBreadcrumbTitle(productState.productData?.globalArticleName ?? '')
      }
    }, [productState.productData])

    const headerConfig = useMemo(() => {
      const priceInfoConfig = getPriceInfo({
        data: productState.productData?.price,
        pageLocale,
        showOldPrices: general?.showOldPrices,
        currentPriceLabelProp: general?.currentPriceLabel,
        oldPriceLabelProp: general?.oldPriceLabel
      })
      const { currentPrice, originalPrice, ...requiredConfig } =
        priceInfoConfig ?? {}
      const priceInfoTextProps = requiredConfig
      const processedPriceProps = Object.fromEntries(
        Object.entries(priceInfoTextProps).map(([propKey, value]) => [
          propKey,
          value && typeof value === 'object' && 'content' in value
            ? {
                ...value,
                content:
                  value.content === null
                    ? undefined
                    : (value.content as string | undefined)
              }
            : value
        ])
      ) as typeof priceInfoTextProps
      return {
        title: productState.productData?.globalArticleName,
        productCode: productState.productData?.globalArticleCode,
        prices: {
          ...processedPriceProps,
          message: productState.productData?.price?.priceDescription
            ? productState.productData.price.priceDescription
            : undefined
        },
        rating: ratesAndReviews?.enabled
      } as Parameters<typeof UIMixAndMatchHeader>[0]
    }, [productState.productData, ratesAndReviews?.enabled])

    const stickyHeaderConfig = useMemo(() => {
      const priceInfoConfig = getPriceInfo({
        data: productState.productData?.price,
        pageLocale,
        showOldPrices: general?.showOldPrices,
        currentPriceLabelProp: general?.currentPriceLabel,
        oldPriceLabelProp: general?.oldPriceLabel
      })
      const { currentPrice, originalPrice } = priceInfoConfig ?? {}
      return {
        productName: productState.productData?.globalArticleName,
        currentPrice: currentPrice ?? '',
        originalPrice: originalPrice ?? '',
        'aria-hidden': true,
        ...(isWizardHeaderMounted && !initialProductData
          ? // Force uif comp to re-render when wizard header ref is ready
            { 'data-wizard-header-ready': 'true' }
          : {})
      }
    }, [productState.productData, isWizardHeaderMounted])

    const handleAttributeChange = (
      newProductAttributeSelectedValues: Record<string, string>,
      currentProductVariant: ProductDataVariant,
      expandedStepId: string
    ) => {
      const productSetReference =
        productState.productData?.productSetReferences?.find(
          (productSetReferenceItem) =>
            productSetReferenceItem?.globalArticleCode?.includes(
              currentProductVariant?.globalArticleCode ?? ''
            )
        )
      const variantsOfProductSetReference =
        productState.productData?.variants?.filter((variant) =>
          productSetReference?.globalArticleCode?.includes(
            variant?.globalArticleCode ?? ''
          )
        )

      const matchingVariant = variantsOfProductSetReference?.find((variant) => {
        if (
          !hasAllMatchingAttributes(variant, newProductAttributeSelectedValues)
        ) {
          return false
        }

        if (general?.groupByColorOrFlavour) {
          return variant?.nuance?.code === currentProductVariant?.nuance?.code
        }

        return (
          splitProductCode(variant?.pmiInternalProductCode ?? '') ===
          splitProductCode(currentProductVariant?.pmiInternalProductCode ?? '')
        )
      })

      if (matchingVariant) {
        // Only update TEMP state - don't commit until selection button is clicked
        setSelectionState((prev) => ({
          ...prev,
          tempStepCurrentVariantMap: prev.tempStepCurrentVariantMap.map(
            (step) =>
              step.stepId === expandedStepId
                ? { ...step, currentVariant: matchingVariant }
                : step
          )
        }))
      }
    }

    const handleColorChange = (
      stepId: string,
      newVariant: ProductDataVariant
    ) => {
      const groupId = stepId.split('_')[0]
      const productSetReference =
        productState.productData?.productSetReferences?.find(
          (psr) => psr?.group?.toString() === groupId
        )
      const productVariantIds = productSetReference?.globalArticleCode ?? []
      const stepVariants = productState.productData?.variants?.filter((v) =>
        productVariantIds.includes(v?.globalArticleCode ?? '')
      )

      const currentStepVariant = selectionState.tempStepCurrentVariantMap?.find(
        (step) => step.stepId === stepId
      )
      const currentVariant = currentStepVariant?.currentVariant

      let variantToSelect = newVariant

      if (currentVariant && attributeSelector && attributeSelector.length > 0) {
        const currentAttributeValues = extractAttributeValues(
          currentVariant,
          attributeSelector
        )

        const matchingVariant = findVariantByColorAndAttributes({
          stepVariants,
          newVariant,
          attributeValues: currentAttributeValues,
          groupByColorOrFlavour: general?.groupByColorOrFlavour
        })

        if (matchingVariant) {
          variantToSelect = matchingVariant
        }
      }
      setSelectionState((prev) => ({
        ...prev,
        tempStepCurrentVariantMap: prev.tempStepCurrentVariantMap.map((step) =>
          getAugmentedStepWithVariant(step, stepId, variantToSelect)
        )
      }))
    }

    useEffect(() => {
      const currentExpandedSteps = expandStepsDescription({
        stepsDescription: wizardSteps?.stepsDescription ?? [],
        productSetReferences: productState.productData?.productSetReferences
      })
      const productInfoList = currentExpandedSteps?.map((step, stepIndex) => {
        const { groupId, expandedStepId } = step
        const productVariant =
          productState.productData?.productSetReferences?.find(
            (productSetReference) =>
              productSetReference?.group?.toString() === groupId
          )
        const productVariantIds = productVariant?.globalArticleCode

        // Use temp state for UI update
        const currentStepVariant =
          selectionState.tempStepCurrentVariantMap?.find(
            (s) => s.stepId === expandedStepId
          )
        const variant = currentStepVariant?.currentVariant

        const variants = productState.productData?.variants?.filter(
          (v) =>
            (productVariantIds ?? [])?.indexOf(v?.globalArticleCode ?? '') > -1
        )
        const calculatedProductTitle =
          wizardSteps?.stepCounterLabel
            ?.replace(REPLACE_INDEX, String(stepIndex + 1))
            ?.replace(REPLACE_AMOUNT, String(currentExpandedSteps?.length)) ??
          ''

        return {
          groupId: step?.groupId,
          productTitle: calculatedProductTitle,
          productSubtitle: variant?.globalArticleName ?? '',
          productImage: getAssetImageUrl(variant?.assets?.[0]) ?? '',
          productDescription: getProductDescription(variant ?? undefined),
          variants
        } as ProductInfoProps & ProductDataVariant
      })
      const updatedSteps = getMappedModelStepsToWizardSteps({
        stepsModel: wizardSteps,
        expandedSteps: currentExpandedSteps,
        productInfoList,
        productStockMap: productState.productStockMap,
        onSelect: (index: number) => {
          // COMMIT temp state to actual state when selection button is clicked
          const updatedCompletionMap = updateCompletionMap(
            wizardUIState.completionMap,
            index
          )

          setSelectionState((prev) => ({
            ...prev,
            // Commit the temp selections to actual selections
            stepCurrentVariantMap: [...prev.tempStepCurrentVariantMap]
          }))

          const nextStepIndex = updatedCompletionMap?.indexOf(false)

          setWizardUIState((prev) => ({
            ...prev,
            completionMap: updatedCompletionMap,
            activeStepIndex: nextStepIndex
          }))

          if (isMobile) {
            if (
              nextStepIndex > -1 &&
              stepRefs.current[wizardUIState.activeStepIndex]
            ) {
              scrollToElement(stepRefs.current[wizardUIState.activeStepIndex])
            } else if (nextStepIndex === -1 && headerRef.current) {
              scrollToElement(headerRef.current)
            }
          }
        },
        onChange: (index: number) => {
          setSelectionState((prev) => ({
            ...prev,
            // Reset temp state to the last "committed" state
            tempStepCurrentVariantMap: [...prev.stepCurrentVariantMap]
          }))

          setWizardUIState((prev) => ({
            ...prev,
            activeStepIndex: index
          }))
        },
        shopConfig: caConfig?.shopConfig ?? {},
        showFlavourProfiles: !!general?.enableFlavourProfile,
        backupGalleryUrl: general?.backupImageUrl,
        enableGalleryLazyLoad: general?.enableLazyLoad ?? false,
        stepCurrentVariantMap: selectionState.tempStepCurrentVariantMap,
        attributeSelector,
        pageLocale,
        groupByFlavour: general?.groupByColorOrFlavour,
        onColorChange: handleColorChange,
        onAttributeChange: handleAttributeChange,
        isSsr,
        id: id ?? '',
        productData: productState?.productData
      })

      setWizardUIState((prev) => ({ ...prev, steps: updatedSteps }))

      // Initial step completion setting
      let adaptedCompletionMap = wizardUIState.completionMap
      if (
        productState.productData &&
        adaptedCompletionMap?.length === 0 &&
        currentExpandedSteps?.length > 0 &&
        productState.isStockUpdated !== undefined
      ) {
        adaptedCompletionMap = currentExpandedSteps?.map((_, index) => {
          return updatedSteps?.[index]?.isCompleted
        })
        setWizardUIState((prev) => ({
          ...prev,
          activeStepIndex: adaptedCompletionMap?.indexOf(false),
          completionMap: adaptedCompletionMap
        }))
      }
    }, [
      productState.isStockUpdated,
      props.model,
      wizardUIState.completionMap,
      selectionState.tempStepCurrentVariantMap,
      productState.productStockMap
    ])

    const lastStepContent = isCompletionFull
      ? {
          model: {
            xfPath: general?.lastStep ?? '',
            xfContent: general?.lastStepXFContent
          }
        }
      : null

    const dataLayerEcommerceProduct = createDataLayerEcommerceProduct(
      productState.productData || ({} as ProductInfo)
    )

    const addToCartMethodConfig = createAddToCartMethodConfig({
      orderType: general?.orderType ?? '',
      setIsButtonDisabled: (disabled: boolean) =>
        setWizardUIState((prev) => ({ ...prev, isButtonDisabled: disabled })),
      setIsButtonLoading: (loading: boolean) =>
        setWizardUIState((prev) => ({ ...prev, isButtonLoading: loading })),
      dataLayerEcommerceProduct,
      productData: productState.productData,
      productEntries:
        // Use "committed" product variants from completed steps
        selectionState.stepCurrentVariantMap
          .filter((_, index) => wizardUIState.completionMap[index])
          .map((step) => step.currentVariant?.globalArticleCode)
          .filter((code): code is string => Boolean(code)),
      productID: productState.productData?.globalArticleCode ?? '',
      caConfig: {
        hybrisConfig: caConfig?.hybrisConfig as HybrisConfig | undefined,
        shopConfig: caConfig?.shopConfig as ShopConfig | undefined,
        market: caConfig?.market,
        lang: caConfig?.hybrisConfig?.language,
        muleSoftConfig: caConfig?.muleSoftConfig as
          MuleSoftConfigType | undefined
      },
      setActiveMiniCart: (isActive: boolean) => {
        setWizardUIState((prev) => ({ ...prev, activeMiniCart: isActive }))
        if (
          (caConfig?.shopConfig as ShopConfig & { redirectToCart?: boolean })
            ?.redirectToCart &&
          isActive
        ) {
          const cartCTAUrl = caConfig?.shopConfig?.cartCTAUrl
          if (cartCTAUrl) {
            window.location.href = cartCTAUrl
          }
        }
      },
      cookies,
      setCookie: ((name, value, options) => {
        setCookie(
          name as
            | typeof GIGYA_COOKIE_UID
            | typeof HYBRIS_ACCESS_TOKEN
            | typeof GIGYA_COOKIE_JWT,
          value,
          options
        )
      }) as SetCookieType,
      cartLocalStorageJSON,
      setCartLocalStorage,
      setErrorMessageContent: ((content) => {
        setErrorHandlingState((prev) => ({
          ...prev,
          errorMessageContent: {
            message: null,
            title:
              content.title ??
              caConfig?.shopConfig?.addToCartErrorMessage ??
              null,
            actions: null
          }
        }))
      }) as SetErrorMessageContentType,
      setDialogErrorMessageIsOpen: (isOpen: boolean) =>
        setErrorHandlingState((prev) => ({
          ...prev,
          dialogErrorMessageIsOpen: isOpen
        })),
      userData,
      productQuantity: 1
    })

    const onATCButtonClick = () => {
      setMarkerProductData(productState.productData)
      handleAddToCart({
        ...addToCartMethodConfig
      })
    }

    useEffect(() => {
      if (
        !isSsr &&
        productState.productData &&
        Object.keys(productState.productData).length > 0
      ) {
        pushDataLayerProductDetailsView(dataLayerEcommerceProduct)
      }
    }, [productState.productData, isSsr])

    const addToCartProperties = {
      buttonProps: {
        onClick: onATCButtonClick,
        label: general?.addToCartLabel,
        loading: wizardUIState.isButtonLoading,
        disable: wizardUIState.isButtonDisabled
      }
      // preOrderMessage: isPreorder ? preOrderMessage : null,
    }

    const configCartErrorMessageDialog = {
      errorMessageContent: {
        ...errorHandlingState.errorMessageContent,
        title: errorHandlingState.errorMessageContent.title ?? undefined
      },
      dialogErrorMessageIsOpen: errorHandlingState.dialogErrorMessageIsOpen,
      setDialogErrorMessageIsOpen: (isOpen: boolean) =>
        setErrorHandlingState((prev) => ({
          ...prev,
          dialogErrorMessageIsOpen: isOpen
        }))
    }

    const wizardProps = {
      activeStepIndex: wizardUIState.activeStepIndex,
      steps: wizardUIState.steps?.map((step, index) => ({
        ...step,
        isCompleted: wizardUIState.completionMap?.[index]
      })),
      finalStepDescription: general?.successLabel ?? '',
      genericContent: lastStepContent ? (
        <ExperienceFragmentContent {...lastStepContent} />
      ) : null,
      addToCart: <AddToCart {...addToCartProperties} />,
      deliveryInfo: deliveryInfoMessage || undefined,
      stepRefs
    }
    const highlightComponentContent =
      (isCompletionFull && general?.showHighlightsOnLastStep) ||
      !isCompletionFull
        ? {
            model: {
              xfPath: general?.sliderProductHighlights ?? '' /* ,
            xfContent: { ...highlightComponent } */
            }
          }
        : null

    const mixAndMatchConfig = {
      ...(productState?.productData
        ? {
            headerProps: { ...headerConfig }
          }
        : {}),
      ...(highlightComponentContent
        ? {
            highlightsComponent: (
              <ExperienceFragmentContent {...highlightComponentContent} />
            )
          }
        : {}),
      ...(wizardUIState.steps?.length
        ? {
            wizard: { ...wizardProps }
          }
        : {}),
      stickyHeader: stickyHeaderConfig,
      headerRef
    }

    return (
      <EditableComponent {...editableProps}>
        {!MixAndMatchEditConfig.isEmpty(props.model) ? (
          <>
            <UIMixAndMatch {...mixAndMatchConfig} />
            {wizardUIState.steps?.length ? (
              <>
                {configMiniCart ? <MiniCart {...configMiniCart} /> : null}
                <CartErrorMessageDialog {...configCartErrorMessageDialog} />
              </>
            ) : null}
          </>
        ) : (
          MixAndMatchEditConfig.emptyLabel
        )}
      </EditableComponent>
    )
  })

// @ts-expect-error Property 'getServerSideProps' does not exist
MixAndMatch.getServerSideProps = async (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props: Record<string, any>
) => {
  const { key /* , general */ } = props
  const cbxConfig = PageConfig?.getConfig(
    PageConfig?.ConfigObjects?.CbxConfig
  ) as AppConfig['cbxConfig'] | null
  if (!cbxConfig) {
    throw new Error('CbxConfig is required')
  }

  const fetchedData = await fetchProductData({
    config: cbxConfig,
    key: key ?? '',
    includeVariants: 'true',
    includeRelationships: 'true',
    includeOffline: 'false',
    includeCrosslinked: 'false',
    ...getServerSideHeaders()
  })
  return {
    ...props,
    initialProductData: { ...fetchedData }
  }
}

// @ts-expect-error Property 'getHeadData' does not exist
MixAndMatch.getHeadData = async (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props: Record<string, any>
): Promise<GetCAndBHeadDataReturnType> => {
  const { initialProductData, pageUrl } = props ?? {}
  const status = initialProductData?.status || 200
  return {
    status,
    seoTitle: initialProductData?.seo?.productsSeoTitle,
    dynamicPageTitle: initialProductData?.globalArticleName,
    seoDescription: initialProductData?.seo?.productsSeoDescription,
    jsonLD: generateProductJsonLD(initialProductData, pageUrl)
  }
}

const MixAndMatchWithErrorBoundary = withErrorBoundary(MixAndMatch, {
  componentName: 'MixAndMatch',
  fallback: <ErrorFallback componentName="MixAndMatch" />
})

export default MixAndMatchWithErrorBoundary
